import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from "@mui/material";
import { Plus, X, Check, CheckCircle2, RotateCcw, CornerDownRight, ArrowRight, GripVertical, HelpCircle, Pencil, Trophy } from "lucide-react";
import { dataService } from "../../../../Services/data/data.service";
import { simulationScenarioTypes } from "../../../../Config/constants";
import { processPriceInput, parsePrice } from "../../../../Utils/priceInput";
import { fmt } from "../../../../Utils/formatCurrency";
import { toast } from "react-toastify";
import Button from "../../../Shared/Buttons/Button";
import Chip from "../../../Shared/ui/Chip";
import Input from "../../../Shared/Input";

// ScenarioType: 1 = Accounts, 2 = PriceDefinitions

// ─── helpers ────────────────────────────────────────────────────────────────

// Shared column widths — keeps the param table and P&L table columns visually aligned.
// Both tables use table-layout:fixed with the same colgroup widths; since they share
// the same container width the first (label) column will also be identical.
const SIM_COL_W = 225; // px — Baseline/Current and every scenario column

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}


function parseLocalNumber(str) {
  if (str == null) return NaN;
  return parseFloat(String(str).replace(/\./g, "").replace(",", "."));
}

// Extract all accounts from profitLossReportData as flat list with current totals
function extractAccounts(profitLossReportData) {
  if (!profitLossReportData) return [];
  const result = [];
  const processCategories = (cats) => {
    if (!cats) return;
    cats.forEach((cat) => {
      if (!cat.accountResults) return;
      cat.accountResults.forEach((acc) => {
        const total = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        result.push({
          id: acc.id,
          name: acc.name,
          categoryName: cat.name,
          isRevenue: cat.isRevenue,
          group: cat.isRevenue ? "Revenue" : "Cost",
          currentValue: total,
        });
      });
    });
  };
  processCategories(profitLossReportData.revenueAccountCategoryResults);
  processCategories(profitLossReportData.costAccountCategoryResults);

  const groupOrder = { Revenue: 0, Cost: 1 };
  result.sort((a, b) => {
    const go = (groupOrder[a.group] ?? 2) - (groupOrder[b.group] ?? 2);
    if (go !== 0) return go;
    const catCmp = (a.categoryName ?? "").localeCompare(b.categoryName ?? "");
    if (catCmp !== 0) return catCmp;
    return a.name.localeCompare(b.name);
  });

  return result;
}

// Extract all price definitions from priceBundles as flat deduped list,
// grouped by Revenue/Cost via P&L data and sorted alphabetically within each group.
function extractPriceDefinitions(priceBundles, profitLossReportData) {
  if (!priceBundles) return [];

  const revenueIds = new Set();
  const costIds = new Set();
  if (profitLossReportData) {
    (profitLossReportData.revenueAccountCategoryResults || []).forEach((cat) => {
      (cat.accountResults || []).forEach((acc) => revenueIds.add(acc.id));
    });
    (profitLossReportData.costAccountCategoryResults || []).forEach((cat) => {
      (cat.accountResults || []).forEach((acc) => costIds.add(acc.id));
    });
  }

  const seen = new Set();
  const result = [];
  priceBundles.forEach((company) => {
    (company.priceBundles || []).forEach((bundle) => {
      (bundle.items || []).forEach((item) => {
        if (!seen.has(item.priceDefinitionId)) {
          seen.add(item.priceDefinitionId);
          // Classify by the item's effective account (COALESCE(pdef.DefaultAccountId, pb.AccountId))
          // so priceDefinitions whose own account differs from the bundle-level account are correctly
          // grouped as Revenue or Cost instead of being shown as Uncategorized.
          const effId = item.effectiveAccountId ?? bundle.accountId;
          const group = revenueIds.has(effId) ? "Revenue" : costIds.has(effId) ? "Cost" : "Uncategorized";
          result.push({
            id: item.priceDefinitionId,
            name: item.priceDefinitionName,
            currentValue: item.price,
            uom: item.unitOfMeasurement || null,
            group,
          });
        }
      });
    });
  });

  const groupOrder = { Revenue: 0, Cost: 1, Uncategorized: 2 };
  result.sort((a, b) => {
    const go = (groupOrder[a.group] ?? 2) - (groupOrder[b.group] ?? 2);
    if (go !== 0) return go;
    return a.name.localeCompare(b.name);
  });

  return result;
}

// Compute projected P&L for accounts-type scenario
function computeAccountsPL(basePLD, items) {
  if (!basePLD) return null;
  const data = deepClone(basePLD);
  const itemMap = {};
  items.forEach((i) => { itemMap[i.itemId] = i.newValue; });

  const processCategories = (cats) => {
    if (!cats) return;
    cats.forEach((cat) => {
      let catTotal = 0;
      (cat.accountResults || []).forEach((acc) => {
        const baseTotal = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        acc.total = itemMap[acc.id] !== undefined ? itemMap[acc.id] : baseTotal;
        catTotal += acc.total;
      });
      cat.total = catTotal;
    });
  };

  processCategories(data.revenueAccountCategoryResults);
  processCategories(data.costAccountCategoryResults);

  const revenueTotal = typeof data.revenueTotal === "object"
    ? Object.values(data.revenueTotal)[0] ?? 0
    : data.revenueTotal ?? 0;
  const costTotal = typeof data.costTotal === "object"
    ? Object.values(data.costTotal)[0] ?? 0
    : data.costTotal ?? 0;

  data.revenueTotal = (data.revenueAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.costTotal = (data.costAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.totalProfit = data.revenueTotal - data.costTotal;
  return data;
}

// Compute projected P&L for price-definitions-type scenario
function computePricePL(basePLD, items, allMeasurements, allPriceBundles) {
  if (!basePLD) return null;
  const data = deepClone(basePLD);
  const pdMap = {};
  items.forEach((i) => { pdMap[i.itemId] = i.newValue; });

  if (!allMeasurements || !allPriceBundles) return data;

  // Build per-account ratios. For bundle measurements each item carries its own
  // effectiveAccountId (COALESCE(pdef.DefaultAccountId, pb.AccountId)), so a single
  // bundle measurement can contribute to multiple accounts. The eventPrice is split
  // proportionally across items using their share of the bundle total.
  const accountRatios = {};
  const addRatio = (accountId, original, projected) => {
    if (!accountId) return;
    if (!accountRatios[accountId]) accountRatios[accountId] = { original: 0, projected: 0 };
    accountRatios[accountId].original += original;
    accountRatios[accountId].projected += projected;
  };

  allMeasurements.forEach((m) => {
    const eventPrice = m.eventPrice || 0;

    if (m.priceBundleId) {
      let bundle = null;
      for (const company of allPriceBundles) {
        const found = (company.priceBundles || []).find((b) => b.id === m.priceBundleId);
        if (found) { bundle = found; break; }
      }
      if (bundle && (bundle.items || []).length > 0) {
        const originalBundleTotal = bundle.items.reduce((s, i) => s + i.price * i.multiplier, 0);
        if (originalBundleTotal > 0) {
          bundle.items.forEach((item) => {
            const newP = pdMap[item.priceDefinitionId] !== undefined ? pdMap[item.priceDefinitionId] : item.price;
            const itemOriginal = (item.price * item.multiplier) / originalBundleTotal;
            const itemProjected = (newP * item.multiplier) / originalBundleTotal;
            addRatio(item.effectiveAccountId, eventPrice * itemOriginal, eventPrice * itemProjected);
          });
        } else {
          addRatio(m.accountId, eventPrice, eventPrice);
        }
      } else {
        addRatio(m.accountId, eventPrice, eventPrice);
      }
    } else {
      addRatio(m.accountId, eventPrice, eventPrice);
    }
  });

  const applyRatios = (cats) => {
    (cats || []).forEach((cat) => {
      let catTotal = 0;
      (cat.accountResults || []).forEach((acc) => {
        const baseTotal = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        const ratio = accountRatios[acc.id];
        acc.total = ratio && ratio.original > 0 ? baseTotal * (ratio.projected / ratio.original) : baseTotal;
        catTotal += acc.total;
      });
      cat.total = catTotal;
    });
  };

  applyRatios(data.revenueAccountCategoryResults);
  applyRatios(data.costAccountCategoryResults);
  data.revenueTotal = (data.revenueAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.costTotal = (data.costAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.totalProfit = data.revenueTotal - data.costTotal;
  return data;
}

function computeScenarioPL(basePLD, scenario, allMeasurements, allPriceBundles) {
  if (!scenario) return null;
  if (scenario.scenarioType === simulationScenarioTypes.ACCOUNTS) {
    return computeAccountsPL(basePLD, scenario.items || []);
  }
  return computePricePL(basePLD, scenario.items || [], allMeasurements, allPriceBundles);
}

function getBaselinePL(profitLossReportData) {
  if (!profitLossReportData || Array.isArray(profitLossReportData) || !profitLossReportData.revenueAccountCategoryResults) return null;
  const data = deepClone(profitLossReportData);

  const normalise = (cats) => {
    (cats || []).forEach((cat) => {
      const catT = typeof cat.total === "object" ? Object.values(cat.total)[0] ?? 0 : cat.total ?? 0;
      cat.total = catT;
      (cat.accountResults || []).forEach((acc) => {
        acc.total = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
      });
    });
  };
  normalise(data.revenueAccountCategoryResults);
  normalise(data.costAccountCategoryResults);

  data.revenueTotal = typeof data.revenueTotal === "object" ? Object.values(data.revenueTotal)[0] ?? 0 : data.revenueTotal ?? 0;
  data.costTotal = typeof data.costTotal === "object" ? Object.values(data.costTotal)[0] ?? 0 : data.costTotal ?? 0;
  data.totalProfit = typeof data.totalProfit === "object" ? Object.values(data.totalProfit)[0] ?? 0 : data.totalProfit ?? 0;
  return data;
}

// Build the projection object (format consumed by ProfitLossReport / InsightsTab)
// from the current paramRows of a scenario.
function buildProjection(scenario, paramRows) {
  if (!scenario || scenario.isBaseline) return false;
  const changed = paramRows.filter((p) => Math.abs(p.editVal - p.currentValue) > 0.001);
  if (changed.length === 0) return false;

  if (scenario.scenarioType === simulationScenarioTypes.ACCOUNTS) {
    return {
      accounts: changed.map((p) => ({
        id: p.id,
        name: p.name,
        isRevenue: p.isRevenue,
        percentage: p.currentValue !== 0 ? (p.editVal / p.currentValue) * 100 : 100,
      })),
    };
  }
  return {
    priceDefinitions: changed.map((p) => ({
      priceDefinitionId: p.id,
      priceDefinitionName: p.name,
      currentPrice: p.currentValue,
      newPrice: p.editVal,
    })),
  };
}

function computeScenarioChanges(scenario, profitLossReportData, allPriceBundles) {
  if (!scenario || scenario.isBaseline || !scenario.items?.length) return { type: null, changes: [] };

  if (scenario.scenarioType === simulationScenarioTypes.ACCOUNTS) {
    const accountMap = {};
    const traverse = (cats) => {
      (cats || []).forEach((cat) => {
        (cat.accountResults || []).forEach((acc) => {
          const total = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
          accountMap[acc.id] = { name: acc.name, currentValue: total };
        });
      });
    };
    if (profitLossReportData && !Array.isArray(profitLossReportData)) {
      traverse(profitLossReportData.revenueAccountCategoryResults);
      traverse(profitLossReportData.costAccountCategoryResults);
    }
    const changes = [];
    scenario.items.forEach((item) => {
      const acc = accountMap[item.itemId];
      if (!acc) return;
      if (Math.abs(item.newValue - acc.currentValue) > 0.001) {
        changes.push({ name: acc.name, from: acc.currentValue, to: item.newValue });
      }
    });
    return { type: "accounts", changes };
  }

  // PRICE_DEFINITIONS
  const pdMap = {};
  (allPriceBundles || []).forEach((company) => {
    (company.priceBundles || []).forEach((bundle) => {
      (bundle.items || []).forEach((item) => {
        if (!pdMap[item.priceDefinitionId]) {
          pdMap[item.priceDefinitionId] = { name: item.priceDefinitionName, currentValue: item.price };
        }
      });
    });
  });
  const changes = [];
  scenario.items.forEach((item) => {
    const pd = pdMap[item.itemId];
    if (!pd) return;
    if (Math.abs(item.newValue - pd.currentValue) > 0.001) {
      changes.push({ name: pd.name, from: pd.currentValue, to: item.newValue });
    }
  });
  return { type: "priceDefinitions", changes };
}

// Returns a scenario's saved value for a param; falls back to the param's current value
function getScenarioParamVal(scenario, paramId, fallback) {
  if (!scenario) return fallback;
  const item = (scenario.items || []).find((i) => i.itemId === paramId);
  return item != null ? item.newValue : fallback;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function NewScenarioModal({ open, onConfirm, onCancel }) {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [type, setType] = useState(simulationScenarioTypes.ACCOUNTS);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName("");
      setType(simulationScenarioTypes.ACCOUNTS);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  return (
    <Dialog open={open} maxWidth={false} classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title}>New scenario</DialogTitle>
        <button className={classes.dialog__closeButton} onClick={onCancel}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          <p className={classes.dialog__sub}>Give the scenario a name and choose which parameters to adjust.</p>
          <div className={classes.inputContainer}> 
            <div className={classes.labelContainer}>
              <label className={classes.labelText}>Name</label>
            </div>
            <Input
              ref={inputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Energy +50%, Machine replacement…"
              onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onConfirm(name.trim(), type); }}
              fullwidth
            />
          </div>
          <div className={classes.inputContainer} style={{ marginTop: 16 }}>
            <div className={classes.labelContainer}>
              <label className={classes.labelText}>Parameter type</label>
            </div>
            <div className={classes.typeRow}>
              <button
                className={`${classes.typeBtn} ${type === simulationScenarioTypes.ACCOUNTS ? classes.typeBtn_active : ""}`}
                onClick={() => setType(simulationScenarioTypes.ACCOUNTS)}
              >
                Accounts
              </button>
              <button
                className={`${classes.typeBtn} ${type === simulationScenarioTypes.PRICE_DEFINITIONS ? classes.typeBtn_active : ""}`}
                onClick={() => setType(simulationScenarioTypes.PRICE_DEFINITIONS)}
              >
                Price definitions
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button disabled={!name.trim()} onClick={() => onConfirm(name.trim(), type)}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function ParamRow({ param, editValue, onChange, onReset, disabled, currency, decimals = 0, uom, showCategory = true, comparisonCols = [], showEditCol = true }) {
  const classes = useStyles();
  const currentVal = param.currentValue ?? 0;
  const scenarioVal = editValue !== undefined ? editValue : currentVal;
  const isChanged = Math.abs(scenarioVal - currentVal) > 0.001;
  const pct = currentVal !== 0 ? ((scenarioVal - currentVal) / Math.abs(currentVal)) * 100 : 0;
  const pctDisplay = pct.toLocaleString("nl-BE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const isRevenue = param.isRevenue === true || param.group === "Revenue";
  const isCost = param.isRevenue === false || param.group === "Cost";
  const chipVariant = !isChanged ? "default" : isRevenue ? (pct > 0 ? "success" : "danger") : (pct > 0 ? "danger" : "success");

  const formatNum = (n) => n.toLocaleString("nl-BE", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const [displayVal, setDisplayVal] = useState(() => formatNum(scenarioVal));
  // Track the last value we propagated up so we can distinguish external prop changes from our own
  const lastPropagatedRef = useRef(scenarioVal);
  const inputRef = useRef(null);
  const cursorPosRef = useRef(null);

  useEffect(() => {
    if (Math.abs(scenarioVal - lastPropagatedRef.current) > 0.0001) {
      lastPropagatedRef.current = scenarioVal;
      setDisplayVal(formatNum(scenarioVal));
    }
  }, [scenarioVal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore cursor after React re-renders the input with the new formatted value
  useLayoutEffect(() => {
    if (cursorPosRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosRef.current, cursorPosRef.current);
      cursorPosRef.current = null;
    }
  });

  const handleChange = (e) => {
    const raw = e.target.value;
    const cursorPos = e.target.selectionStart;

    const formatted = processPriceInput(raw, 2);
    if (formatted === null) return; // invalid char — ignore, keep current display

    // Remap cursor: count significant chars (digits + comma) before cursor in raw,
    // then find the same count in the formatted string (dots are separators and shift positions).
    const sigBefore = (raw.slice(0, cursorPos).match(/[\d,]/g) || []).length;
    let newCursorPos = formatted.length;
    if (sigBefore === 0) {
      newCursorPos = 0;
    } else {
      let sigCount = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/[\d,]/.test(formatted[i])) {
          if (++sigCount === sigBefore) { newCursorPos = i + 1; break; }
        }
      }
    }
    cursorPosRef.current = newCursorPos;

    setDisplayVal(formatted);
    const parsed = parsePrice(formatted);
    const numVal = parsed !== null ? parsed : 0;
    lastPropagatedRef.current = numVal;
    onChange(param.id, numVal);
  };

  const handleBlur = () => {
    // Reformat with thousands separators on blur
    const parsed = parsePrice(displayVal);
    if (parsed !== null) setDisplayVal(formatNum(parsed));
  };

  return (
    <tr className={classes.paramRow}>
      <td className={classes.paramCell}>
        <div className={classes.param__name}>{param.name}</div>
        {showCategory && param.categoryName && <div className={classes.param__cat}>{param.categoryName}</div>}
      </td>
      <td className={`${classes.paramCell} ${classes.paramCell_mono}`}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span>{fmt(currentVal, currency, decimals)}</span>
          {uom && <Chip variant="default" size="extraSmall">{uom}</Chip>}
        </div>
      </td>
      {comparisonCols.map((col, ci) => {
        const colDiff = col.value - col.baseline;
        const colChanged = Math.abs(colDiff) > 0.001;
        const colPct = col.baseline !== 0 ? (colDiff / Math.abs(col.baseline)) * 100 : 0;
        const colPctDisplay = (colPct > 0 ? "+" : "") + colPct.toLocaleString("nl-BE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
        const colChipVariant = col.isRevenue ? (colPct > 0 ? "success" : "danger") : (colPct > 0 ? "danger" : "success");
        return (
          <td key={ci} className={`${classes.paramCell} ${classes.paramCell_mono}`} style={{ textAlign: "right" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
              <span>{colChanged ? <Chip variant={colChipVariant} size="extraSmall">{colPctDisplay}</Chip> : null}</span>
              <span style={colChanged ? { color: colChipVariant === "success" ? "var(--color-text-success)" : "var(--color-text-danger)", fontWeight: 500 } : undefined}>{fmt(col.value, currency, decimals)}</span>
            </div>
          </td>
        );
      })}
      {showEditCol && (
        <td className={classes.paramCell}>
          <div className={classes.param__inputWrap}>
            <span className={classes.param__unit}>{currency?.symbol ?? "€"}</span>
            <input
              ref={inputRef}
              className={`${classes.param__input} ${isChanged ? classes.param__input_changed : ""}`}
              value={displayVal}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {isChanged && (
              <button
                className={classes.param__resetBtn}
                onClick={() => onReset && onReset(param.id)}
                title="Reset to original value"
                tabIndex={-1}
              >
                <RotateCcw size={14} />
              </button>
            )}
          </div>
        </td>
      )}
      <td className={classes.paramCell}>
        <Chip
          variant={chipVariant}
          size="extraSmall"
        >
          {!isChanged ? "—" : (pct > 0 ? "+" : "") + pctDisplay + "%"}
        </Chip>
      </td>
    </tr>
  );
}

function ScenarioChangesTooltip({ changes, type, currency }) {
  const decimals = type === "priceDefinitions" ? 2 : 0;
  return (
    <div style={{ minWidth: 200, maxWidth: 340 }}>
      <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 6, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.6px" }}>
        {type === "priceDefinitions" ? "Price definition changes" : "Account changes"}
      </div>
      {changes.map((c, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 12,
            marginBottom: i < changes.length - 1 ? 5 : 0,
          }}
        >
          <span style={{ fontSize: 12 }}>{c.name}</span>
          <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap", opacity: 0.85 }}>
            {fmt(c.from, currency, decimals)}&nbsp;→&nbsp;{fmt(c.to, currency, decimals)}
          </span>
        </div>
      ))}
    </div>
  );
}

function PLTable({ baseline, orderedSimCols, activeScenarioId, currency, onReorderCols, bestScenarioId }) {
  const classes = useStyles();
  const [dragSrcIdx, setDragSrcIdx] = useState(null);
  const [dragTargetIdx, setDragTargetIdx] = useState(null);

  if (!baseline) return null;

  const showDelta = orderedSimCols.length > 0;

  // Helpers to safely extract totals from Dapper's object-or-scalar shape
  const catTotal = (cat) =>
    cat ? (typeof cat.total === "object" ? Object.values(cat.total)[0] ?? 0 : cat.total ?? 0) : 0;
  const getCats = (pl, isRevenue) =>
    (isRevenue ? pl?.revenueAccountCategoryResults : pl?.costAccountCategoryResults) || [];

  const handleDragStart = (ci) => {
    if (orderedSimCols[ci]?.id === activeScenarioId) return;
    setDragSrcIdx(ci);
  };
  const handleDragOver = (e, ci) => {
    if (orderedSimCols[ci]?.id === activeScenarioId) return;
    e.preventDefault();
    setDragTargetIdx(ci);
  };
  const handleDragEnd = () => { setDragSrcIdx(null); setDragTargetIdx(null); };
  const handleDrop = (ci) => {
    if (orderedSimCols[ci]?.id === activeScenarioId) { setDragSrcIdx(null); setDragTargetIdx(null); return; }
    if (dragSrcIdx !== null && dragSrcIdx !== ci) {
      const newIds = orderedSimCols.map((s) => s.id);
      const [moved] = newIds.splice(dragSrcIdx, 1);
      newIds.splice(ci, 0, moved);
      onReorderCols(newIds);
    }
    setDragSrcIdx(null);
    setDragTargetIdx(null);
  };

  const deltaCell = (baseVal, bestVal, isRevenue = true) => {
    const diff = bestVal - baseVal;
    if (Math.abs(diff) < 1) return <td className={`${classes.plCell} ${classes.plCell_delta}`}>—</td>;
    const isImprovement = isRevenue ? diff > 0 : diff < 0;
    const cls = isImprovement ? classes.plVal_pos : classes.plVal_neg;
    return (
      <td className={`${classes.plCell} ${classes.plCell_delta} ${cls}`}>
        {diff > 0 ? "+" : ""}{fmt(diff, currency, 2)}
      </td>
    );
  };

  const valCell = (val, baseVal, isSection, isTotal, isRevenue = true) => {
    const changed = Math.abs(val - baseVal) > 1;
    const betterClass = isRevenue ? classes.plVal_pos : classes.plVal_neg;
    const worseClass = isRevenue ? classes.plVal_neg : classes.plVal_pos;
    const cls = changed ? (val > baseVal ? betterClass : worseClass) : "";
    const pct = changed && baseVal !== 0 ? ((val - baseVal) / Math.abs(baseVal)) * 100 : 0;
    const pctDisplay = (pct > 0 ? "+" : "") + pct.toLocaleString("nl-BE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
    const chipVariant = isRevenue ? (pct > 0 ? "success" : "danger") : (pct > 0 ? "danger" : "success");
    return (
      <td className={`${classes.plCell} ${isSection ? classes.plCell_section : ""} ${isTotal ? classes.plCell_total : ""} ${cls}`}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
          <span>{changed && <Chip variant={chipVariant} size="extraSmall">{pctDisplay}</Chip>}</span>
          {fmt(val, currency, 2)}
        </div>
      </td>
    );
  };

  const renderSection = (label, isRevenue) => {
    const baseCats = ((isRevenue ? baseline.revenueAccountCategoryResults : baseline.costAccountCategoryResults) || [])
      .slice()
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    const colSpan = 2 + orderedSimCols.length + (showDelta ? 1 : 0);

    const rows = [
      <tr key={label} className={classes.plSectionRow}>
        <td className={`${classes.plCell} ${classes.plCell_section} ${isRevenue ? classes.plCell_section_revenue : classes.plCell_section_cost}`} colSpan={colSpan}>
          {label}
        </td>
      </tr>,
    ];

    baseCats.forEach((baseCat) => {
      const baseT = catTotal(baseCat);

      // Pre-compute each sim column's category total (needed for relative % and delta)
      const simCatTs = orderedSimCols.map((simCol) => {
        const simCat = getCats(simCol._pl ?? baseline, isRevenue).find((c) => c.id === baseCat.id) || baseCat;
        return catTotal(simCat);
      });

      rows.push(
        <tr key={baseCat.id} className={classes.plCategoryRow}>
          <td className={`${classes.plCell} ${classes.plCell_category}`}>{baseCat.name}</td>
          {valCell(baseT, baseT, false, false, isRevenue)}
          {orderedSimCols.map((simCol, ci) =>
            valCell(simCatTs[ci], baseT, false, false, isRevenue)
          )}
          {showDelta && deltaCell(baseT, isRevenue ? Math.max(baseT, ...simCatTs) : Math.min(baseT, ...simCatTs), isRevenue)}
        </tr>
      );

      (baseCat.accountResults || []).slice().sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")).forEach((baseAcc) => {
        const baseAccT = catTotal(baseAcc);

        const simAccTs = orderedSimCols.map((simCol) => {
          const simCat = getCats(simCol._pl ?? baseline, isRevenue).find((c) => c.id === baseCat.id) || baseCat;
          const simAcc = (simCat.accountResults || []).find((a) => a.id === baseAcc.id) || baseAcc;
          return catTotal(simAcc);
        });

        rows.push(
          <tr key={`${baseCat.id}-${baseAcc.id}`} className={classes.plRow}>
            <td className={`${classes.plCell} ${classes.plCell_account}`}><span className={classes.plCell_account_inner}><ArrowRight className={classes.plCell_account_icon} size={12} />{baseAcc.name}</span></td>
            {valCell(baseAccT, baseAccT, false, false, isRevenue)}
            {orderedSimCols.map((simCol, ci) =>
              valCell(simAccTs[ci], baseAccT, false, false, isRevenue)
            )}
            {showDelta && deltaCell(baseAccT, isRevenue ? Math.max(baseAccT, ...simAccTs) : Math.min(baseAccT, ...simAccTs), isRevenue)}
          </tr>
        );
      });
    });

    return rows;
  };

  const baseRevT = baseline.revenueTotal ?? 0;
  const baseCostT = baseline.costTotal ?? 0;
  const baseProfit = baseline.totalProfit ?? 0;
  const simRevTs = orderedSimCols.map((s) => s._pl?.revenueTotal ?? 0);
  const simCostTs = orderedSimCols.map((s) => s._pl?.costTotal ?? 0);
  const simProfits = orderedSimCols.map((s) => s._pl?.totalProfit ?? 0);

  return (
    <table className={`${classes.plTable} no-stripes`} style={{ tableLayout: "fixed" }}>
      <colgroup>
        <col />
        <col style={{ width: SIM_COL_W }} />
        {orderedSimCols.map((s) => <col key={s.id} style={{ width: SIM_COL_W }} />)}
        {showDelta && <col />}
      </colgroup>
      <thead>
        <tr>
          <th className={classes.plTh} style={{ textAlign: "left" }}>P&amp;L Component</th>
          <th className={`${classes.plTh} ${classes.plTh_baseline}`}>Baseline</th>
          {orderedSimCols.map((s, ci) => {
            const isActive = activeScenarioId === s.id;
            const isBest = bestScenarioId === s.id;
            return (
            <th
              key={s.id}
              className={`${classes.plTh} ${isActive ? classes.plTh_scenario : (isBest ? classes.plTh_best : classes.plTh_compare)} ${dragTargetIdx === ci && dragSrcIdx !== ci ? classes.plTh_dragOver : ""}`}
              draggable={!isActive}
              onDragStart={() => handleDragStart(ci)}
              onDragOver={(e) => handleDragOver(e, ci)}
              onDragEnd={handleDragEnd}
              onDrop={() => handleDrop(ci)}
              style={{ cursor: isActive ? "default" : "grab" }}
            >
              <span style={{ display: "flex", alignItems: "center", justifyContent: isActive ? "flex-end" : "space-between", overflow: "hidden" }}>
                {!isActive && <GripVertical size={13} style={{ opacity: 0.45, flexShrink: 0 }} />}
                <span style={{ display: "flex", alignItems: "center", gap: 4, overflow: "hidden" }}>
                  {isBest && <Trophy size={12} style={{ flexShrink: 0, color: isActive ? "#d97706" : "rgba(255,255,255,0.95)" }} />}
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                  {s._changes?.changes?.length > 0 && (
                    <Tooltip
                      title={<ScenarioChangesTooltip changes={s._changes.changes} type={s._changes.type} currency={currency} />}
                      placement="bottom-end"
                      arrow
                    >
                      <span
                        style={{ display: "inline-flex", cursor: "help", flexShrink: 0 }}
                        onDragStart={(e) => e.stopPropagation()}
                      >
                        <HelpCircle size={13} style={{ opacity: 0.65 }} />
                      </span>
                    </Tooltip>
                  )}
                </span>
              </span>
            </th>
          );
          })}
          {showDelta && <th className={`${classes.plTh} ${classes.plTh_delta}`}>Delta best</th>}
        </tr>
      </thead>
      <tbody>
        {renderSection("Revenue", true)}
        <tr className={classes.plSubtotalRow}>
          <td className={classes.plCell}><strong>Total Revenue</strong></td>
          {valCell(baseRevT, baseRevT, false, false, true)}
          {orderedSimCols.map((s, ci) => valCell(simRevTs[ci], baseRevT, false, false, true))}
          {showDelta && deltaCell(baseRevT, Math.max(baseRevT, ...simRevTs), true)}
        </tr>

        {renderSection("Costs", false)}
        <tr className={classes.plSubtotalRow}>
          <td className={classes.plCell}><strong>Total Costs</strong></td>
          {valCell(baseCostT, baseCostT, false, false, false)}
          {orderedSimCols.map((s, ci) => valCell(simCostTs[ci], baseCostT, false, false, false))}
          {showDelta && deltaCell(baseCostT, Math.min(baseCostT, ...simCostTs), false)}
        </tr>

        <tr className={classes.plTotalRow}>
          <td className={`${classes.plCell} ${classes.plCell_total}`}>Gross Profit</td>
          {valCell(baseProfit, baseProfit, false, true)}
          {orderedSimCols.map((s, ci) => valCell(simProfits[ci], baseProfit, false, true))}
          {showDelta && deltaCell(baseProfit, Math.max(baseProfit, ...simProfits), true)}
        </tr>
      </tbody>
    </table>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ProjectionsTab({
  graphId,
  profitLossReportData,
  allPriceBundles,
  allMeasurements,
  currency,
  login,
  activeProjection,
  handleSetActiveProjection,
  // Lifted state — survives tab switches
  selectedScenarioId,
  onSelectedScenarioChange,
  appliedScenarioId,
  onAppliedScenarioChange,
}) {
  const classes = useStyles();

  // Saved scenarios loaded from backend (no baseline here)
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);

  const activeScenarioId = selectedScenarioId;
  const setActiveScenarioId = onSelectedScenarioChange;

  const [compareIds, setCompareIds] = useState([]);
  const [simColOrder, setSimColOrder] = useState([]);

  // Unsaved param edits { itemId: newValue }
  const [editParams, setEditParams] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync: if activeProjection was cleared externally, clear the applied tracker
  useEffect(() => {
    if (!activeProjection) onAppliedScenarioChange(null);
  }, [activeProjection]); // eslint-disable-line react-hooks/exhaustive-deps

  const baselinePL = getBaselinePL(profitLossReportData);

  const allParams = (() => {
    if (activeScenarioId !== "baseline" && savedScenarios.find((s) => s.id === activeScenarioId)) {
      const s = savedScenarios.find((sc) => sc.id === activeScenarioId);
      if (s.scenarioType === simulationScenarioTypes.ACCOUNTS) return extractAccounts(profitLossReportData);
      return extractPriceDefinitions(allPriceBundles, profitLossReportData);
    }
    // Baseline active — derive params from first compare scenario so the table can still show
    const firstCompare = savedScenarios.find((s) => compareIds.includes(s.id));
    if (firstCompare) {
      if (firstCompare.scenarioType === simulationScenarioTypes.ACCOUNTS) return extractAccounts(profitLossReportData);
      return extractPriceDefinitions(allPriceBundles, profitLossReportData);
    }
    return [];
  })();

  // Build scenario list: baseline + saved
  const scenarioList = [
    {
      id: "baseline",
      name: "Current (baseline)",
      isBaseline: true,
      scenarioType: simulationScenarioTypes.ACCOUNTS,
      items: [],
      _pl: baselinePL,
    },
    ...savedScenarios.map((s) => ({
      ...s,
      _pl: computeScenarioPL(baselinePL, s, allMeasurements, allPriceBundles),
      _changes: computeScenarioChanges(s, profitLossReportData, allPriceBundles),
    })),
  ];

  // Compute active scenario's "preview" P&L (incorporates unsaved edits)
  const activeScenario = scenarioList.find((s) => s.id === activeScenarioId) ?? scenarioList[0];
  const activeScenarioWithEdits = {
    ...activeScenario,
    items: allParams.map((p) => ({
      itemId: p.id,
      newValue: editParams[p.id] !== undefined ? editParams[p.id] : (activeScenario.items.find((i) => i.itemId === p.id)?.newValue ?? p.currentValue),
    })),
  };
  const activePL = activeScenario.isBaseline
    ? baselinePL
    : computeScenarioPL(baselinePL, activeScenarioWithEdits, allMeasurements, allPriceBundles);

  const compareScenarios = compareIds
    .map((id) => scenarioList.find((s) => s.id === id))
    .filter(Boolean);

  // Ordered sim columns shared by both P&L table and param comparison columns
  const showSimulation = !activeScenario?.isBaseline;
  const allSimCols = showSimulation
    ? [{ ...activeScenario, _pl: activePL }, ...compareScenarios]
    : [...compareScenarios];
  const allSimMap = new Map(allSimCols.map((s) => [s.id, s]));
  let orderedSimCols = [
    ...simColOrder.filter((id) => allSimMap.has(id)).map((id) => allSimMap.get(id)),
    ...allSimCols.filter((s) => !simColOrder.includes(s.id)),
  ];
  // Pin the active/editing scenario to the last position (just before Delta)
  if (showSimulation) {
    const activeIdx = orderedSimCols.findIndex((s) => s.id === activeScenario.id);
    if (activeIdx !== -1 && activeIdx !== orderedSimCols.length - 1) {
      const [activeCol] = orderedSimCols.splice(activeIdx, 1);
      orderedSimCols.push(activeCol);
    }
  }

  // Best scenario: highest simulated profit among loaded columns (only meaningful when 2+)
  const bestScenarioId = orderedSimCols.length > 1
    ? orderedSimCols.reduce((best, s) =>
        (s._pl?.totalProfit ?? 0) > (best._pl?.totalProfit ?? 0) ? s : best
      ).id
    : null;

  // Compare-only columns: when baseline is active all orderedSimCols are compare cols; otherwise exclude the active editing scenario
  const isComparing = compareIds.length > 0;
  const compareSimCols = isComparing
    ? (activeScenario?.isBaseline ? orderedSimCols : orderedSimCols.filter((s) => s.id !== activeScenario.id))
    : [];
  const paramColCount = isComparing ? 2 + orderedSimCols.length + 1 : 4;

  // ── data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!graphId) return;
    setLoadingScenarios(true);
    dataService
      .getGraphSimulationScenarios(graphId)
      .then((data) => {
        setSavedScenarios(Array.isArray(data) ? data.map((s) => ({
          ...s,
          scenarioType: s.scenarioType,
          items: (s.items || []).map((i) => ({ itemId: i.itemId, newValue: Number(i.newValue) })),
        })) : []);
      })
      .catch(() => setSavedScenarios([]))
      .finally(() => setLoadingScenarios(false));
  }, [graphId]);

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleSelectScenario = (id) => {
    setActiveScenarioId(id);
    setEditParams({});
  };

  const handleUpdateParam = (paramId, value) => {
    setEditParams((prev) => ({ ...prev, [paramId]: value }));
  };

  const handleResetEdits = () => setEditParams({});

  const handleResetParam = (paramId) => {
    const param = allParams.find((p) => p.id === paramId);
    if (!param) return;
    setEditParams((prev) => ({ ...prev, [paramId]: param.currentValue }));
  };

  const handleSave = async () => {
    if (activeScenario.isBaseline) return;
    setSaving(true);
    try {
      const items = allParams.map((p) => ({
        itemId: p.id,
        newValue: editParams[p.id] !== undefined ? editParams[p.id] : (activeScenario.items.find((i) => i.itemId === p.id)?.newValue ?? p.currentValue),
      }));
      const result = await dataService.updateGraphSimulationScenario(
        activeScenario.id,
        activeScenario.name,
        items,
        login?.userid ?? 0
      );
      if (result?.success !== false) {
        setSavedScenarios((prev) =>
          prev.map((s) =>
            s.id === activeScenario.id ? { ...s, items } : s
          )
        );
        setEditParams({});
        toast.success("Scenario saved", { autoClose: 2000 });
      } else {
        toast.error(result.message || "Save failed", { autoClose: 3000 });
      }
    } catch {
      toast.error("Save failed", { autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleNewScenario = async (name, type) => {
    setModalOpen(false);
    try {
      const result = await dataService.createGraphSimulationScenario(
        graphId,
        name,
        type,
        login?.userid ?? 0
      );
      if (result?.success !== false && result?.scenarioId) {
        const newScenario = { id: result.scenarioId, name, scenarioType: type, items: [] };
        setSavedScenarios((prev) => [...prev, newScenario]);
        setActiveScenarioId(result.scenarioId);
        setEditParams({});
      } else {
        toast.error(result?.message || "Could not create scenario", { autoClose: 3000 });
      }
    } catch {
      toast.error("Could not create scenario", { autoClose: 3000 });
    }
  };

  const handleDeleteScenario = async (id) => {
    try {
      await dataService.deleteGraphSimulationScenario(id, login?.userid ?? 0);
      setSavedScenarios((prev) => prev.filter((s) => s.id !== id));
      setCompareIds((prev) => prev.filter((x) => x !== id));
      if (activeScenarioId === id) setActiveScenarioId("baseline");
    } catch {
      toast.error("Could not delete scenario", { autoClose: 3000 });
    }
  };

  const handleApply = () => {
    const projection = buildProjection(activeScenario, paramRows);
    if (!projection) {
      toast.info("No changes to apply — parameters match the baseline.", { autoClose: 3000 });
      return;
    }
    handleSetActiveProjection({ ...projection, name: activeScenario.name });
    onAppliedScenarioChange(activeScenarioId);
  };

  const handleClearProjection = () => {
    handleSetActiveProjection(null);
    onAppliedScenarioChange(null);
  };

  const handleToggleCompare = (id) => {
    if (id === activeScenarioId) return;
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [...prev.slice(1), id];
      return [...prev, id];
    });
  };

  const hasUnsavedEdits = Object.keys(editParams).length > 0;

  // ── param list for active scenario ────────────────────────────────────────

  const paramRows = allParams.map((p) => {
    const savedItem = activeScenario.items.find((i) => i.itemId === p.id);
    const editVal = editParams[p.id] !== undefined ? editParams[p.id] : (savedItem?.newValue ?? p.currentValue);
    return { ...p, editVal };
  });

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className={classes.root}>
      {/* SIDEBAR */}
      <div className={classes.sidebar}>
        <div className={classes.sidebar__title}>Scenarios</div>

        {loadingScenarios ? (
          <div className={classes.sidebar__loading}>Loading…</div>
        ) : (
          scenarioList.map((s) => {
            const impact = s.isBaseline ? null : ((s._pl?.totalProfit ?? 0) - (baselinePL?.totalProfit ?? 0));
            const isActive = s.id === activeScenarioId;
            const isComparing = compareIds.includes(s.id);
            return (
              <div
                key={s.id}
                className={`${classes.scenarioCard} ${isActive ? classes.scenarioCard_active : ""} ${isComparing && !isActive ? classes.scenarioCard_comparing : ""}`}
                onClick={() => !isComparing && handleSelectScenario(s.id)}
                style={isComparing && !isActive ? { cursor: "default" } : undefined}
              >
                <div className={classes.scenarioCard__nameRow}>
                  <span className={classes.scenarioCard__name}>{s.name}</span>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {isComparing && !isActive && (
                      <button
                        className={classes.scenarioCard__editBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCompareIds((prev) => prev.filter((x) => x !== s.id));
                          handleSelectScenario(s.id);
                        }}
                        title="Edit this scenario"
                      >
                        <Pencil size={12} />
                      </button>
                    )}
                    {!s.isBaseline && bestScenarioId === s.id && (
                      <span className={classes.scenarioCard__bestBadge}>
                        <Trophy size={9} />Best
                      </span>
                    )}
                    {!s.isBaseline && appliedScenarioId === s.id && (
                      <span className={classes.scenarioCard__appliedBadge}>Active</span>
                    )}
                    {s.isBaseline && <span className={classes.scenarioCard__tag}>baseline</span>}
                  </div>
                </div>
                <div className={classes.scenarioCard__summary}>
                  {s.isBaseline
                    ? `Gross profit: ${fmt(baselinePL?.totalProfit ?? 0, currency)}`
                    : impact != null
                    ? `Impact: ${impact >= 0 ? "+" : ""}${fmt(impact, currency)}`
                    : "—"}
                </div>
                {!s.isBaseline && (
                  <div className={classes.scenarioCard__actions} onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="extraSmall"
                      variant={isComparing ? "outlined" : "secondary"}
                      onClick={() => handleToggleCompare(s.id)}
                      startIcon={isComparing ? <Check /> : null}
                      disabled={isActive}
                    >
                      Compare
                    </Button>
                    <Button
                      size="extraSmall"
                      variant="destructive"
                      onClick={() => handleDeleteScenario(s.id)}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}

        <div className={classes.addScenario} onClick={() => setModalOpen(true)}>
          <Plus size={14} style={{ marginRight: 4 }} />
          New scenario
        </div>

        {compareIds.length > 0 && (
          <div className={classes.compareBar}>
            <div className={classes.compareBar__title}>Comparing</div>
            {compareIds.map((id) => {
              const s = scenarioList.find((x) => x.id === id);
              if (!s) return null;
              const impact = (s._pl?.totalProfit ?? 0) - (baselinePL?.totalProfit ?? 0);
              return (
                <div key={id} className={classes.compareBar__item}>
                  <span>{s.name}</span>
                  <span style={{ color: impact >= 0 ? "#4ade80" : "#f87171" }}>
                    {impact >= 0 ? "+" : ""}{fmt(impact, currency)}
                  </span>
                </div>
              );
            })}
            <button className={classes.compareBar__clear} onClick={() => setCompareIds([])}>Clear</button>
          </div>
        )}
      </div>

      {/* MAIN */}
      <div className={classes.main}>
        {/* ACTIVE PROJECTION INDICATOR */}
        {activeProjection && appliedScenarioId && (
          <div className={classes.projectionBanner}>
            <div className={classes.projectionBanner__left}>
              <CheckCircle2 size={15} className={classes.projectionBanner__icon} />
              <span>
                Simulation active
                {scenarioList.find((s) => s.id === appliedScenarioId) && (
                  <> — <strong>{scenarioList.find((s) => s.id === appliedScenarioId)?.name}</strong></>
                )}
              </span>
            </div>
            <Button variant="secondary" size="small" onClick={handleClearProjection}>Clear</Button>
          </div>
        )}
        {/* PARAMETER EDITOR */}
        <div className={classes.editorCard}>
          <div className={classes.editorCard__head}>
            <div>
              <div className={classes.editorCard__title}>
                Parameters — {activeScenario.name}
                {!activeScenario.isBaseline && (
                  <span className={classes.typeChip}>
                    {activeScenario.scenarioType === simulationScenarioTypes.ACCOUNTS ? "Accounts" : "Price definitions"}
                  </span>
                )}
              </div>
              <div className={classes.editorCard__sub}>
                {activeScenario.isBaseline
                  ? (isComparing ? "Compare mode — parameters are read-only" : "Baseline — select a scenario to adjust parameters")
                  : "Adjust parameters · P&L updates automatically"}
              </div>
            </div>
          </div>
          <table className={`${classes.paramTable} no-stripes`} style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col />
              <col style={{ width: SIM_COL_W }} />
              {isComparing
                ? orderedSimCols.map((s) => <col key={s.id} style={{ width: SIM_COL_W }} />)
                : <col style={{ width: SIM_COL_W }} />
              }
              <col />
            </colgroup>
            <thead>
              <tr>
                <th className={classes.paramTh}>Parameter</th>
                <th className={classes.paramTh}>Current</th>
                {isComparing
                  ? orderedSimCols.map((s) => {
                      const isActiveCol = s.id === activeScenarioId;
                      const isBestCol = bestScenarioId === s.id;
                      return (
                        <th key={s.id} className={`${classes.paramTh} ${isActiveCol ? classes.paramTh_active : (isBestCol ? classes.paramTh_best : classes.paramTh_compare)}`} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            {isBestCol && <Trophy size={11} style={{ flexShrink: 0, color: isActiveCol ? "#d97706" : "rgba(255,255,255,0.9)" }} />}
                            {s.name}
                          </span>
                        </th>
                      );
                    })
                  : <th className={classes.paramTh}>Scenario</th>
                }
                <th className={classes.paramTh}>Delta</th>
              </tr>
            </thead>
            <tbody>
              {activeScenario.isBaseline && !isComparing && (
                <tr>
                  <td colSpan={paramColCount} className={classes.paramEmpty}>
                    Select a scenario from the sidebar to edit parameters.
                  </td>
                </tr>
              )}
              {(!activeScenario.isBaseline || isComparing) && paramRows.length === 0 && (
                <tr>
                  <td colSpan={paramColCount} className={classes.paramEmpty}>
                    No parameters available.
                  </td>
                </tr>
              )}
              {(!activeScenario.isBaseline || isComparing) && paramRows.length > 0 && (() => {
                const isPriceDef = activeScenario.scenarioType === simulationScenarioTypes.PRICE_DEFINITIONS;
                const hasMultipleGroups = new Set(paramRows.map((p) => p.group)).size > 1;
                let lastGroup = null;
                let lastCategory = null;
                return paramRows.flatMap((p) => {
                  const rows = [];
                  if (p.group && (hasMultipleGroups || (isPriceDef && p.group !== "Uncategorized")) && p.group !== lastGroup) {
                    lastGroup = p.group;
                    lastCategory = null;
                    rows.push(
                      <tr key={`group-${p.group}`} className={classes.paramGroupRow}>
                        <td colSpan={paramColCount} className={`${classes.paramGroupCell} ${p.group === "Revenue" ? classes.paramGroupCell_revenue : p.group === "Cost" ? classes.paramGroupCell_cost : ""}`}>{p.group}</td>
                      </tr>
                    );
                  }
                  if (!isPriceDef && p.categoryName && p.categoryName !== lastCategory) {
                    lastCategory = p.categoryName;
                    rows.push(
                      <tr key={`cat-${p.group}-${p.categoryName}`} className={classes.paramCategoryRow}>
                        <td colSpan={paramColCount} className={classes.paramCategoryCell}>{p.categoryName}</td>
                      </tr>
                    );
                  }
                  const comparisonCols = compareSimCols.map((simCol) => ({
                    value: getScenarioParamVal(simCol, p.id, p.currentValue),
                    baseline: p.currentValue,
                    isRevenue: p.group === "Revenue",
                  }));
                  rows.push(
                    <ParamRow
                      key={p.id}
                      param={p}
                      editValue={p.editVal}
                      onChange={handleUpdateParam}
                      onReset={handleResetParam}
                      disabled={activeScenario.isBaseline}
                      showEditCol={!activeScenario.isBaseline}
                      currency={currency}
                      decimals={isPriceDef ? 2 : 0}
                      uom={isPriceDef ? p.uom : undefined}
                      showCategory={false}
                      comparisonCols={comparisonCols}
                    />
                  );
                  return rows;
                });
              })()}
            </tbody>
          </table>
          {!activeScenario.isBaseline && (
            <div className={classes.editorCard__footer}>
              <Button variant="ghost" size="small" onClick={handleResetEdits} disabled={!hasUnsavedEdits}>
                Reset
              </Button>
              <div className={classes.editorCard__footerRight}>
                <Button
                  className={classes.applyBtn}
                  onClick={handleApply}
                  startIcon={appliedScenarioId === activeScenarioId && activeProjection && !hasUnsavedEdits ? <Check /> : null}
                >
                  {appliedScenarioId === activeScenarioId && activeProjection && !hasUnsavedEdits
                    ? "Applied"
                    : "Apply to all tabs"}
                </Button>
                <Button variant="primary" loading={saving} onClick={handleSave}>
                  Save scenario
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* P&L IMPACT TABLE */}
        <div className={classes.plCard}>
          <div className={classes.plCard__head}>
            <div className={classes.plCard__title}>P&amp;L Statement</div>
          </div>
          <div className={classes.plCard__tableWrap}>
            <PLTable
              baseline={baselinePL}
              orderedSimCols={orderedSimCols}
              activeScenarioId={activeScenario.id}
              currency={currency}
              onReorderCols={setSimColOrder}
              bestScenarioId={bestScenarioId}
            />
          </div>
        </div>
      </div>

      <NewScenarioModal
        open={modalOpen}
        onConfirm={handleNewScenario}
        onCancel={() => setModalOpen(false)}
      />
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    minHeight: 500,
    gap: 0,
  },

  // Sidebar
  sidebar: {
    width: 260,
    flexShrink: 0,
    background: "#fff",
    borderRight: "1px solid var(--color-border-light)",
    paddingRight: "16px",
    alignSelf: "flex-start",
    position: "sticky",
    top: 76,
  },
  sidebar__title: {
    fontSize: 12,
    fontFamily: "'DM Mono', 'Courier New', monospace",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: "var(--color-text-secondary)",
    marginBottom: 12,
  },
  sidebar__loading: {
    fontSize: 13,
    color: "#9ca3af",
    padding: "8px 0",
  },

  // Scenario cards
  scenarioCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 8,
    cursor: "pointer",
    transition: "border-color 0.15s",
    "&:hover": { borderColor: "#c8922a" },
  },
  scenarioCard_active: {
    borderColor: "#c8922a",
    background: "#fdf5e8",
  },
  scenarioCard_comparing: {
    borderColor: "#1a56db",
    background: "#e8f0fd",
    "&:hover": { borderColor: "#1a56db" },
  },
  scenarioCard__nameRow: {
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  scenarioCard__name: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
  },
  scenarioCard__tag: {
    fontSize: 10,
    color: "#9ca3af",
  },
  scenarioCard__appliedBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: "#15803d",
    background: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: 4,
    padding: "1px 6px",
  },
  scenarioCard__bestBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: "#92400e",
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    borderRadius: 4,
    padding: "1px 6px",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
  },
  scenarioCard__editBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: 4,
    border: "none",
    background: "var(--color-bg-card)",
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    padding: 0,
    transition: "all 0.15s",
    "&:hover": {
      background: "var(--color-bg-disabled)",
    },
  },
  scenarioCard__summary: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 4,
  },
  scenarioCard__actions: {
    display: "flex",
    gap: 4,
    marginTop: 6,
  },

  addScenario: {
    border: "1px dashed #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    textAlign: "center",
    fontSize: 14,
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    transition: "all 0.15s",
    "&:hover": { borderColor: "#c8922a", color: "#c8922a" },
  },

  compareBar: {
    background: "#07142d",
    borderRadius: 8,
    padding: "10px 12px",
    marginTop: 4,
  },
  compareBar__title: {
    fontSize: 11,
    color: "rgba(255,255,255,0.6)",
    marginBottom: 6,
  },
  compareBar__item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#fff",
    marginBottom: 3,
  },
  compareBar__clear: {
    fontSize: 11,
    color: "rgba(255,255,255,0.4)",
    cursor: "pointer",
    background: "none",
    border: "none",
    textDecoration: "underline",
    marginTop: 6,
    fontFamily: "inherit",
    "&:hover": { color: "#fff" },
  },

  // Main area
  main: {
    flex: 1,
    minWidth: 0,
    paddingLeft: 16,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  // Editor card
  editorCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
  },
  editorCard__head: {
    padding: "16px",
    borderBottom: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editorCard__title: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  editorCard__sub: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 2,
  },
  typeChip: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 4,
    background: "#fdf5e8",
    border: "1px solid #c8922a",
    color: "#c8922a",
  },

  // Param table
  paramTable: {
    width: "100%",
    borderCollapse: "collapse",
    "& tr:nth-child(even) td": { background: "#fff" },
  },
  paramTh: {
    fontSize: 11,
    color: "#6b7280",
    fontWeight: 500,
    padding: "8px 16px",
    textAlign: "left",
    borderBottom: "1px solid #DCDCDC",
    background: "#fff",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  paramRow: {
    "&:hover $paramCell": { background: "#f9fafb" },
    "&:last-child $paramCell": { borderBottom: "none" },
  },
  paramCell: {
    padding: "8px 16px",
    borderBottom: "1px solid #DCDCDC",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  paramCell_mono: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    fontSize: 14,
    color: "var(--color-text-secondary)",
  },
  param__name: {
    fontSize: 13,
    color: "var(--color-text-primary)",
  },
  param__cat: {
    fontSize: 11,
    color: "var(--color-text-secondary)",
  },
  param__inputWrap: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  param__unit: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
  },
  param__input: {
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: 14,
    width: 128,
    fontFamily: "'DM Mono', 'Courier New', monospace",
    outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: "var(--color-primary-light)" },
  },
  param__input_changed: {
    borderColor: "var(--color-primary-light)",
    background: "var(--color-primary-tint)",
  },
  param__resetBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    background: "#f9fafb",
    color: "#9ca3af",
    cursor: "pointer",
    padding: 0,
    transition: "all 0.15s",
    "&:hover": {
      borderColor: "#c8922a",
      color: "#c8922a",
      background: "#fdf5e8",
    },
  },
  editorCard__footer: {
    padding: "16px",
    borderTop: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editorCard__footerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  applyBtn: {
    "&&": {
      background: "#07142d",
      color: "#fff",
      fontWeight: 500,
    },
    "&&:hover": { opacity: 0.85 },
  },
  // Active projection indicator banner
  projectionBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: 10,
    padding: "10px 16px",
  },
  projectionBanner__left: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#15803d",
  },
  projectionBanner__icon: {
    color: "#16a34a",
    flexShrink: 0,
  },
  paramGroupRow: {
    background: "#f9fafb",
  },
  paramGroupCell: {
    padding: "6px 16px",
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    borderBottom: "1px solid #DCDCDC",
    borderTop: "1px solid #DCDCDC",
  },
  paramGroupCell_revenue: { color: "#16a34a" },
  paramGroupCell_cost: { color: "#dc2626" },
  paramCategoryRow: {
    background: "#fff",
  },
  paramCategoryCell: {
    padding: "5px 20px",
    fontSize: 11,
    fontWeight: 600,
    color: "#374151",
    borderBottom: "1px solid #DCDCDC",
  },
  paramEmpty: {
    padding: "20px 16px",
    fontSize: 13,
    color: "#9ca3af",
    textAlign: "center",
  },

  // P&L card
  plCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
  },
  plCard__head: {
    padding: "14px 16px",
    borderBottom: "1px solid #f3f4f6",
  },
  plCard__title: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
  },
  plCard__tableWrap: {
    overflowX: "auto",
  },

  // P&L table
  plTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
    "& tr:nth-child(even) td": { background: "#fff" },
  },
  plTh: {
    padding: "8px 14px",
    textAlign: "right",
    fontSize: 11,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    borderBottom: "1px solid #DCDCDC",
    background: "#fff",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  plTh_baseline: {
    background: "#f9fafb",
    color: "var(--color-text-secondary)",
    fontWeight: 500,
  },
  plTh_scenario: {
    color: "var(--color-text-secondary)",
    fontWeight: 500,
  },
  plTh_compare: {
    background: "var(--color-bg-brand)",
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    userSelect: "none",
  },
  plTh_best: {
    background: "#d97706",
    color: "#fff",
    fontWeight: 500,
    userSelect: "none",
  },
  plTh_dragOver: {
    boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.8)",
    opacity: 0.8,
  },
  plTh_delta: {
    background: "rgba(0,0,0,0.02)",
    color: "var(--color-text-secondary)",
  },
  plRow: {
    "&:hover $plCell": { background: "#f9fafb" },
  },
  plSectionRow: {
    background: "#fff",
  },
  plSubtotalRow: {
    "& td": { background: "var(--color-bg-page) !important" },
  },
  plTotalRow: {
    background: "var(--color-bg-card)",
    "& $plCell": {
      borderTop: "2px solid var(--color-primary)",
    },
  },
  plCell: {
    padding: "8px 14px",
    borderBottom: "1px solid #DCDCDC",
    textAlign: "right",
    fontFamily: "'DM Mono', 'Courier New', monospace",
    whiteSpace: "nowrap",
    overflow: "hidden",
    "&:first-child": {
      textAlign: "left",
      fontFamily: "inherit",
      color: "var(--color-text-primary)",
    },
  },
  plCell_section: {
    fontWeight: 600,
    fontSize: 12,
    color: "#07142d",
  },
  plCell_section_revenue: { color: "#16a34a !important" },
  plCell_section_cost: { color: "#dc2626 !important" },
  plCell_total: {
    fontWeight: 600,
    fontFamily: "'DM Mono', 'Courier New', monospace",
    fontSize: 14,
    color: "var(--color-primary)",
  },
  plCell_delta: {
    background: "rgba(0,0,0,0.02)",
  },
  plCategoryRow: {
    "&:hover $plCell": { background: "#f3f4f6" },
  },
  plCell_category: {
    fontWeight: 600,
    fontSize: 14,
    color: "#374151",
  },
  plCell_account: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    paddingLeft: 16,
  },
  plCell_account_inner: {
    display: "inline-flex",
    alignItems: "center",
  },
  plCell_account_icon: {
    marginRight: 6,
    color: "var(--color-text-secondary)",
  },
  plVal_pos: { color: "var(--color-text-success)", fontWeight: 500 },
  plVal_neg: { color: "var(--color-text-danger)", fontWeight: 500 },

  // Dialog (matches PriceDefinitionDialog design system)
  dialogPaper: {
    width: "400px !important",
    height: "auto",
    maxHeight: "80vh !important",
    borderRadius: "6px !important",
  },
  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },
  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",
    padding: "0px !important",
  },
  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
    padding: "3px !important",
    height: "25px !important",
    borderRadius: "3px",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },
  },
  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",
    "&::-webkit-scrollbar": { display: "none" },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },
  dialog__content: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    width: "100%",
  },
  dialog__sub: {
    fontSize: "14px",
    color: "#6b7280",
    margin: "0 0 16px 0",
  },
  inputContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  labelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  labelText: {
    fontSize: "14px",
    color: "var(--color-text-primary)",
    opacity: 0.8,
  },
  typeRow: {
    display: "flex",
    gap: "8px",
  },
  typeBtn: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: "4px",
    border: "1px solid #EDEDED",
    background: "#F8F8F8",
    fontSize: "14px",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "#6b7280",
    transition: "all 0.15s",
    "&:hover": { borderColor: "var(--color-text-primary)" },
  },
  typeBtn_active: {
    borderColor: "var(--color-text-primary)",
    background: "#fff",
    color: "var(--color-text-primary)",
    fontWeight: 500,
  },
  paramTh_active: {
    background: "#fdf5e8",
    color: "#c8922a",
    fontWeight: "normal",
    textAlign: "left",
  },
  paramTh_compare: {
    background: "var(--color-bg-brand)",
    color: "var(--color-text-secondary)",
    fontWeight: "normal",
    textAlign: "right",
  },
  paramTh_best: {
    background: "#d97706",
    color: "#fff",
    fontWeight: "normal",
    textAlign: "right",
  },
}));
